import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box, Button, IconButton, Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Drawer,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useVirtualizer} from '@tanstack/react-virtual';
import {WpHeader, WpHeaderProps} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common';
import {useCompanyAnnualStore} from '@/store/audit';
import type {TestRow} from '@/services/auditService/interface/wp-row';
import {toNumber} from '@/utils/dataUtils';
import {DatePicker, InputField, MoneyField} from '@/components/common';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import SamplePlanningBanner from '@/pages/components/audit/adjustment/adjust/dialogs/common/SamplePlanningBanner';
import AutoSelectSampleDialog, {type AutoSelectSampleParams} from '@/pages/components/audit/adjustment/adjust/dialogs/common/AutoSelectSampleDialog';
import {countFilledTestRows} from '@/pages/components/audit/adjustment/adjust/dialogs/common/countFilledTestRows';
import {SampleSelectionVariant, getQI13VO, getQE13VO, formatAmount} from '@/services/auditService/materialityService';
import {
    candidatesToTestRows,
    SampleInvoiceCandidate,
    selectTransactionSample,
} from '@/pages/components/audit/adjustment/adjust/dialogs/common/selectTransactionSample';

/** Above this, render with virtual list (Glass Label QI1 ~800 rows freezes without it). */
const VIRTUALIZE_THRESHOLD = 50;
const ROW_HEIGHT = 44;
const VIRTUAL_OVERSCAN = 10;

const COL = {
    idx: 40,
    date: 170,
    inv: 155,
    desc: 210,
    amount: 160,
    action: 44,
} as const;

const GRID_TEMPLATE = `${COL.idx}px ${COL.date}px ${COL.inv}px minmax(${COL.desc}px, 1fr) ${COL.amount}px ${COL.action}px`;

export type TestDrawerProps = {
    open: boolean;
    onClose: () => void;
    header: Pick<WpHeaderProps, 'itemName' | 'periodTo' | 'preparedBy' | 'reviewedBy' | 'dateText'>;
    annualId: number;
    rows: TestRow[];
    allowAddRows?: boolean;
    onRowsChange?: (rows: TestRow[]) => void;
    readOnly?: boolean;
    samplePlanning?: {
        variant: SampleSelectionVariant;
        companyId: number;
        cutOffCount?: number;
        /** GL invoice population for auto sample selection. */
        population?: SampleInvoiceCandidate[];
        /** QI1.3 monetary population FS code (default PL-TURN-SI). */
        populationCode?: string;
    };
};

const isRowFilled = (r: TestRow): boolean => {
    const hasText = (r.invNo || '').trim() !== '' || (r.description || '').trim() !== '';
    const hasDate = !!r.date;
    const amt = toNumber(r.amount);
    const hasAmt = amt !== null;
    return hasText || hasDate || hasAmt;
};

type RowEditorsProps = {
    row: TestRow;
    idx: number;
    readOnly: boolean;
    currencySymbol: string;
    showDelete: boolean;
    onUpdate: (id: number | null | undefined, patch: Partial<TestRow>) => void;
    onRemove: (id: number | null | undefined) => void;
};

const TestRowEditors = React.memo(function TestRowEditors({
    row,
    idx,
    readOnly,
    currencySymbol,
    showDelete,
    onUpdate,
    onRemove,
}: RowEditorsProps) {
    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography variant="body2">{idx + 1}</Typography>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5}}>
                {readOnly ? (
                    <Typography variant="body2">{row.date || ''}</Typography>
                ) : (
                    <DatePicker
                        value={row.date}
                        onChange={(v) => onUpdate(row.id, {date: v})}
                        size="small"
                        variant="outlined"
                        compact
                    />
                )}
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', px: 0.5}}>
                {readOnly ? (
                    <Typography variant="body2">{row.invNo}</Typography>
                ) : (
                    <InputField
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={row.invNo}
                        onChange={(e) => onUpdate(row.id, {invNo: e.target.value})}
                    />
                )}
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', px: 0.5}}>
                {readOnly ? (
                    <Typography variant="body2">{row.description}</Typography>
                ) : (
                    <InputField
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={row.description}
                        onChange={(e) => onUpdate(row.id, {description: e.target.value})}
                    />
                )}
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5}}>
                <MoneyField
                    value={row.amount ?? 0}
                    showCents
                    align="center"
                    compact
                    currencySymbol={currencySymbol}
                    editable={!readOnly}
                    onChangeNumber={(num) => onUpdate(row.id, {amount: num})}
                    onChangeDisplay={(disp) => onUpdate(row.id, {amount: disp})}
                />
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                {!readOnly && showDelete ? (
                    <IconButton size="small" color="error" onClick={() => onRemove(row.id)}>
                        <DeleteOutlineIcon fontSize="small"/>
                    </IconButton>
                ) : null}
            </Box>
        </>
    );
});

export default function TestDrawer({
    open,
    onClose,
    header,
    annualId,
    rows,
    onRowsChange,
    allowAddRows = true,
    readOnly = false,
    samplePlanning,
}: TestDrawerProps) {
    const [currencySymbol, setCurrencySymbol] = useState<string>('');
    const [autoSelecting, setAutoSelecting] = useState(false);
    const [autoSelectDialogOpen, setAutoSelectDialogOpen] = useState(false);
    const [suggestedSampleSize, setSuggestedSampleSize] = useState<number | null>(null);
    const [autoSelectSummary, setAutoSelectSummary] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const emitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wasOpenRef = useRef(false);

    const defaultRow: TestRow = useMemo(() => ({
        id: 0,
        date: null,
        invNo: '',
        description: '',
        amount: 0,
    }), []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cur = await useCompanyAnnualStore.getState().getPresentationCurrency(Number(annualId));
                if (!cancelled) setCurrencySymbol(cur || '');
            } catch {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [annualId]);

    const [data, setData] = useState<TestRow[]>(() => (
        Array.isArray(rows) && rows.length > 0 ? rows : [defaultRow]
    ));

    // Sync from props only when the drawer opens — avoid reset on every parent emit (800-row freeze).
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setData(Array.isArray(rows) && rows.length > 0 ? rows : [defaultRow]);
        }
        if (!open && wasOpenRef.current && emitTimerRef.current) {
            clearTimeout(emitTimerRef.current);
            emitTimerRef.current = null;
        }
        wasOpenRef.current = open;
    }, [open, rows, defaultRow]);

    useEffect(() => () => {
        if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
    }, []);

    const emit = useCallback((next: TestRow[]) => {
        const filtered = (next || []).filter(isRowFilled);
        onRowsChange?.(filtered);
    }, [onRowsChange]);

    const emitNow = useCallback((next: TestRow[]) => {
        if (emitTimerRef.current) {
            clearTimeout(emitTimerRef.current);
            emitTimerRef.current = null;
        }
        emit(next);
    }, [emit]);

    /** Large lists: debounce parent sync so typing does not re-render SalesIncomeWp each key. */
    const emitMaybeDebounced = useCallback((next: TestRow[]) => {
        if ((next?.length ?? 0) < VIRTUALIZE_THRESHOLD) {
            emitNow(next);
            return;
        }
        if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
        emitTimerRef.current = setTimeout(() => {
            emitTimerRef.current = null;
            emit(next);
        }, 300);
    }, [emit, emitNow]);

    const totalsMemo = useMemo(
        () => (data || []).reduce((acc, r) => acc + (toNumber(r.amount) ?? 0), 0),
        [data],
    );
    const transactionFilledCount = useMemo(() => countFilledTestRows(data), [data]);
    const shouldVirtualize = (data?.length ?? 0) >= VIRTUALIZE_THRESHOLD;

    const rowVirtualizer = useVirtualizer({
        count: shouldVirtualize ? data.length : 0,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: VIRTUAL_OVERSCAN,
        getItemKey: (index) => {
            const id = data[index]?.id;
            return id != null ? id : index;
        },
        enabled: shouldVirtualize && open,
    });

    const handleSave = () => {
        emitNow(data);
        onClose();
    };

    const handleCloseDiscard = () => {
        if (emitTimerRef.current) {
            clearTimeout(emitTimerRef.current);
            emitTimerRef.current = null;
            emit(data);
        }
        onClose();
    };

    const updateRow = useCallback((id: number | null | undefined, patch: Partial<TestRow>) => {
        if (id == null) return;
        setData((prev) => {
            const next = prev.map((r) => (r.id === id ? {...r, ...patch} : r));
            emitMaybeDebounced(next);
            return next;
        });
    }, [emitMaybeDebounced]);

    const addRow = useCallback(() => {
        setData((prev) => {
            const minId = Math.min(...(prev.map((r) => Number(r.id) || 0)), 0);
            const nextId = minId - 1;
            const newRow: TestRow = {id: nextId, date: null, invNo: '', description: '', amount: 0};
            const next = [...prev, newRow];
            emitMaybeDebounced(next);
            return next;
        });
        requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el) el.scrollTop = el.scrollHeight;
        });
    }, [emitMaybeDebounced]);

    const removeRow = useCallback((id: number | null | undefined) => {
        if (id == null) return;
        setData((prev) => {
            const filtered = prev.filter((r) => r.id !== id);
            const next = filtered.length > 0
                ? filtered
                : [{id: -1, date: null, invNo: '', description: '', amount: null}];
            emitMaybeDebounced(next);
            return next;
        });
    }, [emitMaybeDebounced]);

    useEffect(() => {
        if (!open) setAutoSelectSummary(null);
    }, [open]);

    const handleOpenAutoSelectDialog = useCallback(async () => {
        const population = samplePlanning?.population;
        if (!samplePlanning || !population?.length) {
            setAutoSelectSummary('No GL invoice population available for this working paper.');
            return;
        }
        if (transactionFilledCount > 0) {
            const ok = window.confirm(
                'Replace existing transaction test rows with auto-selected samples?',
            );
            if (!ok) return;
        }

        try {
            const vo = samplePlanning.variant === 'QE1.3'
                ? await getQE13VO(samplePlanning.companyId, annualId)
                : await getQI13VO(samplePlanning.companyId, annualId, samplePlanning.populationCode);
            setSuggestedSampleSize(Number(vo.actualSampleSize ?? vo.sampleSize ?? 0) || null);
        } catch {
            setSuggestedSampleSize(null);
        }
        setAutoSelectDialogOpen(true);
    }, [samplePlanning, transactionFilledCount, annualId]);

    const handleAutoSelectConfirm = useCallback(async (params: AutoSelectSampleParams) => {
        const population = samplePlanning?.population;
        if (!samplePlanning || !population?.length) {
            setAutoSelectSummary('No GL invoice population available for this working paper.');
            setAutoSelectDialogOpen(false);
            return;
        }

        setAutoSelecting(true);
        setAutoSelectSummary(null);
        setAutoSelectDialogOpen(false);
        try {
            const vo = samplePlanning.variant === 'QE1.3'
                ? await getQE13VO(samplePlanning.companyId, annualId)
                : await getQI13VO(samplePlanning.companyId, annualId, samplePlanning.populationCode);
            const pm = Number(vo.performanceMateriality ?? 0);
            const result = selectTransactionSample(population, params.mode === 'count'
                ? {
                    performanceMateriality: pm,
                    mode: 'count',
                    maxCount: params.itemCount,
                }
                : {
                    performanceMateriality: pm,
                    mode: 'coverage',
                    coverageRatio: params.coveragePercent / 100,
                });
            const mapped = candidatesToTestRows(result.selected, annualId) as TestRow[];
            const next = allowAddRows
                ? [...mapped, {id: -1, date: null, invNo: '', description: '', amount: null}]
                : mapped;
            setData(next);
            emitNow(next);
            const pct = result.populationTotal > 0
                ? ((result.selectedTotal / result.populationTotal) * 100).toFixed(1)
                : '0';
            const modeSummary = params.mode === 'count'
                ? `target ${params.itemCount} item${params.itemCount === 1 ? '' : 's'}`
                : `target ${params.coveragePercent}% monetary coverage`;
            setAutoSelectSummary(
                `Selected ${result.selected.length} items (${modeSummary}; `
                + `≥PM: ${result.abovePmCount}, residual: ${result.residualPickedCount}). `
                + `Coverage ${formatAmount(result.selectedTotal)} / ${formatAmount(result.populationTotal)} (${pct}%). `
                + `PM=${formatAmount(pm)}.`,
            );
        } catch (e: any) {
            setAutoSelectSummary(e?.message || 'Auto-select failed');
        } finally {
            setAutoSelecting(false);
        }
    }, [
        samplePlanning,
        annualId,
        allowAddRows,
        emitNow,
    ]);

    const canAutoSelect = !!samplePlanning?.population?.length && !readOnly;

    const headerRow = (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: GRID_TEMPLATE,
                alignItems: 'center',
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                py: 0.5,
                px: 0.5,
            }}
        >
            <Box/>
            <Typography variant="body2" align="center" fontWeight={600}>Date</Typography>
            <Typography variant="body2" align="center" fontWeight={600}>Inv#</Typography>
            <Typography variant="body2" align="center" fontWeight={600}>Description</Typography>
            <Typography variant="body2" align="center" fontWeight={600}>Amount</Typography>
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
                {!readOnly && allowAddRows && (
                    <IconButton size="small" color="primary" onClick={addRow}>
                        <AddCircleOutlineRoundedIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>
        </Box>
    );

    const renderVirtualBody = () => {
        const virtualItems = rowVirtualizer.getVirtualItems();
        return (
            <Box
                ref={scrollRef}
                sx={{flex: 1, overflow: 'auto', minHeight: 0}}
            >
                {headerRow}
                <Box
                    sx={{
                        height: rowVirtualizer.getTotalSize(),
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {virtualItems.map((virtualRow) => {
                        const row = data[virtualRow.index];
                        if (!row) return null;
                        const idx = virtualRow.index;
                        // Allow deleting any sample row (incl. auto-selected); keep at least one row.
                        const showDelete = data.length > 1;
                        return (
                            <Box
                                key={virtualRow.key}
                                data-index={virtualRow.index}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                    display: 'grid',
                                    gridTemplateColumns: GRID_TEMPLATE,
                                    alignItems: 'center',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    px: 0.5,
                                    '&:hover': {bgcolor: 'action.hover'},
                                }}
                            >
                                <TestRowEditors
                                    row={row}
                                    idx={idx}
                                    readOnly={readOnly}
                                    currencySymbol={currencySymbol}
                                    showDelete={showDelete}
                                    onUpdate={updateRow}
                                    onRemove={removeRow}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    const renderPlainTable = () => (
        <TableContainer component={Box} sx={{flex: 1, overflow: 'auto', minHeight: 0}} ref={scrollRef}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow sx={{paddingLeft: 1, paddingRight: 1}}>
                        <TableCell align="center" sx={{width: COL.idx}}/>
                        <TableCell align="center" sx={{minWidth: COL.date}}>Date</TableCell>
                        <TableCell align="center" sx={{minWidth: COL.inv}}>Inv#</TableCell>
                        <TableCell align="center" sx={{minWidth: COL.desc}}>Description</TableCell>
                        <TableCell align="center" sx={{minWidth: COL.amount}}>Amount</TableCell>
                        <TableCell align="center" sx={{width: COL.action}}>
                            {!readOnly && allowAddRows && (
                                <IconButton size="small" color="primary" onClick={addRow}>
                                    <AddCircleOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody sx={{'& .MuiTableCell-root': {paddingTop: '1px', paddingBottom: '1px'}}}>
                    {(data || []).map((row, idx) => {
                        const showDelete = data.length > 1;
                        return (
                            <TableRow key={row.id ?? idx} hover>
                                <TableCell align="center">
                                    <Typography variant="body2">{idx + 1}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    {readOnly ? (
                                        <Typography variant="body2">{row.date || ''}</Typography>
                                    ) : (
                                        <DatePicker
                                            value={row.date}
                                            onChange={(v) => updateRow(row.id, {date: v})}
                                            size="small"
                                            variant="outlined"
                                            compact
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="left">
                                    {readOnly ? (
                                        <Typography variant="body2">{row.invNo}</Typography>
                                    ) : (
                                        <InputField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={row.invNo}
                                            onChange={(e) => updateRow(row.id, {invNo: e.target.value})}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="left">
                                    {readOnly ? (
                                        <Typography variant="body2">{row.description}</Typography>
                                    ) : (
                                        <InputField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={row.description}
                                            onChange={(e) => updateRow(row.id, {description: e.target.value})}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.amount ?? 0}
                                        showCents
                                        align="center"
                                        compact
                                        currencySymbol={currencySymbol}
                                        editable={!readOnly}
                                        onChangeNumber={(num) => updateRow(row.id, {amount: num})}
                                        onChangeDisplay={(disp) => updateRow(row.id, {amount: disp})}
                                    />
                                </TableCell>
                                <TableCell align="center" sx={{width: COL.action}}>
                                    {!readOnly && showDelete ? (
                                        <IconButton size="small" color="error" onClick={() => removeRow(row.id)}>
                                            <DeleteOutlineIcon fontSize="small"/>
                                        </IconButton>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <>
        <Drawer
            anchor="left"
            open={open}
            onClose={handleCloseDiscard}
            hideBackdrop
            disableEnforceFocus
            sx={{
                zIndex: (theme) => theme.zIndex.modal,
                '& .MuiDrawer-paper': {
                    width: '80%',
                    maxWidth: 850,
                    minWidth: 600,
                },
            }}
        >
            <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="h6">
                        Transaction
                        {shouldVirtualize ? (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ml: 1}}>
                                ({data.length} rows)
                            </Typography>
                        ) : null}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {canAutoSelect && (
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={autoSelecting}
                                onClick={handleOpenAutoSelectDialog}
                            >
                                {autoSelecting ? 'Selecting…' : 'Auto-select sample'}
                            </Button>
                        )}
                        {!readOnly && (
                            <Button size="small" variant="contained" onClick={handleSave}>
                                Save
                            </Button>
                        )}
                        <IconButton onClick={handleCloseDiscard}>
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </Box>

                <Box sx={{px: 3, pt: 2}}>
                    <WpHeader {...header}/>
                </Box>

                <Divider sx={{mt: 2}}/>

                <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, px: 3, py: 2}}>
                    {samplePlanning && (
                        <SamplePlanningBanner
                            open={open}
                            variant={samplePlanning.variant}
                            companyId={samplePlanning.companyId}
                            annualId={annualId}
                            currentCount={transactionFilledCount}
                            currentLabel="Transaction"
                            siblingCount={samplePlanning.cutOffCount}
                            siblingLabel="Cut-off"
                            populationCode={samplePlanning.populationCode}
                        />
                    )}
                    {autoSelectSummary && (
                        <Alert
                            severity="info"
                            sx={{mb: 1.5}}
                            onClose={() => setAutoSelectSummary(null)}
                        >
                            {autoSelectSummary}
                        </Alert>
                    )}
                    {shouldVirtualize ? renderVirtualBody() : renderPlainTable()}
                    <Box sx={{mt: 2, display: 'flex', justifyContent: 'flex-end', pr: 2, flexShrink: 0}}>
                        <Typography variant="body2" color="text.secondary">
                            Total:&nbsp;
                            <b>
                                {Number(totalsMemo).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </b>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Drawer>
        <AutoSelectSampleDialog
            open={autoSelectDialogOpen}
            populationCount={samplePlanning?.population?.length ?? 0}
            suggestedCount={suggestedSampleSize}
            onClose={() => setAutoSelectDialogOpen(false)}
            onConfirm={handleAutoSelectConfirm}
        />
        </>
    );
}
